import { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import TeamAuswahl from '../../components/TeamAuswahl';
import { NFL_TEAMS, DIVISIONS, getTeamsByConference } from '../../data/nflTeams';
import './BonusAuswertung.css';

// Admin: Bonusfragen auswerten.
// 1. "Vorschlag laden" holt die richtigen Antworten aus den Spieldaten
//    (Cloud Function bonusfragenVorschlag), 2. Admin prüft/ergänzt (MVP),
// 3. "Probelauf" zeigt die Punkte je Spieler ohne zu schreiben,
// 4. "Auswerten" schreibt (Cloud Function bonusfragenAuswerten).

const divKey = (name) => `division_${name.replace(/\s/g, '_')}`;
const LEER = { preSeasonPredictions: {}, playoffPredictions: {} };

function BonusAuswertung({ saisonId, config, gespeicherteLoesung }) {
  const functions = getFunctions(undefined, 'europe-west3');
  const [antworten, setAntworten] = useState(LEER);
  const [hinweise, setHinweise] = useState([]);
  const [quelle, setQuelle] = useState(null);
  const [laeuft, setLaeuft] = useState(null); // 'vorschlag' | 'probe' | 'auswerten'
  const [probe, setProbe] = useState(null);
  const [status, setStatus] = useState('');
  const [bestaetigen, setBestaetigen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [rosterLaedt, setRosterLaedt] = useState(false);

  // Gespeicherte Lösung als Ausgangspunkt
  useEffect(() => {
    setAntworten({
      preSeasonPredictions: gespeicherteLoesung?.preSeasonPredictions || {},
      playoffPredictions: gespeicherteLoesung?.playoffPredictions || {}
    });
    setProbe(null);
    setHinweise([]);
    setQuelle(null);
    setStatus('');
  }, [saisonId, gespeicherteLoesung]);

  const pre = antworten.preSeasonPredictions;
  const po = antworten.playoffPredictions;
  const setPre = (feld, wert) => { setProbe(null); setAntworten(a => ({ ...a, preSeasonPredictions: { ...a.preSeasonPredictions, [feld]: wert } })); };
  const setPo = (feld, wert) => { setProbe(null); setAntworten(a => ({ ...a, playoffPredictions: { ...a.playoffPredictions, [feld]: wert } })); };

  // MVP-Kader
  const mvpTeam = pre.mvpTeam;
  useEffect(() => {
    if (!mvpTeam) { setRoster([]); return undefined; }
    let abgebrochen = false;
    setRosterLaedt(true);
    fetch(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${NFL_TEAMS[mvpTeam]?.espnId}/roster`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Roster nicht erreichbar'))))
      .then(data => {
        const athletes = [];
        (Array.isArray(data.athletes) ? data.athletes : []).forEach(g => { if (Array.isArray(g.items)) athletes.push(...g.items); });
        const liste = athletes
          .map(a => ({ id: a.id || a.displayName, name: a.displayName || a.fullName || '', position: a.position?.abbreviation || '', number: a.jersey || '-' }))
          .filter(p => p.name)
          .sort((a, b) => (parseInt(a.number, 10) || 999) - (parseInt(b.number, 10) || 999));
        if (!abgebrochen) setRoster(liste);
      })
      .catch(() => { if (!abgebrochen) setRoster([]); })
      .finally(() => { if (!abgebrochen) setRosterLaedt(false); });
    return () => { abgebrochen = true; };
  }, [mvpTeam]);

  const rufe = async (name, daten) => {
    const fn = httpsCallable(functions, name);
    const res = await fn(daten);
    return res.data;
  };

  const handleVorschlag = async () => {
    setLaeuft('vorschlag');
    setStatus('');
    try {
      const r = await rufe('bonusfragenVorschlag', { saisonId });
      const v = r.vorschlag || LEER;
      // MVP aus der gespeicherten Lösung behalten, Rest aus dem Vorschlag
      setAntworten(a => ({
        preSeasonPredictions: {
          ...v.preSeasonPredictions,
          mvpTeam: a.preSeasonPredictions.mvpTeam,
          mvpPlayer: a.preSeasonPredictions.mvpPlayer,
          mvpPlayerName: a.preSeasonPredictions.mvpPlayerName
        },
        playoffPredictions: { ...v.playoffPredictions }
      }));
      setHinweise(r.hinweise || []);
      setQuelle(r.quelle || null);
      setProbe(null);
      setStatus('Vorschlag geladen – bitte prüfen und den MVP eintragen.');
    } catch (error) {
      setStatus(`❌ Vorschlag fehlgeschlagen: ${error.message}`);
    } finally {
      setLaeuft(null);
    }
  };

  const handleProbe = async () => {
    setLaeuft('probe');
    setStatus('');
    try {
      const r = await rufe('bonusfragenAuswerten', { saisonId, korrekteAntworten: antworten, trockenlauf: true });
      setProbe(r.ergebnisse || []);
      setStatus(`Probelauf: ${r.anzahl} Spieler berechnet, nichts geschrieben.`);
    } catch (error) {
      setStatus(`❌ Probelauf fehlgeschlagen: ${error.message}`);
    } finally {
      setLaeuft(null);
    }
  };

  const handleAuswerten = async () => {
    setLaeuft('auswerten');
    setStatus('');
    setBestaetigen(false);
    try {
      const r = await rufe('bonusfragenAuswerten', { saisonId, korrekteAntworten: antworten, trockenlauf: false });
      setStatus(`✅ Ausgewertet: ${r.anzahl} Spieler. Lösung gespeichert.`);
    } catch (error) {
      setStatus(`❌ Auswertung fehlgeschlagen: ${error.message}`);
    } finally {
      setLaeuft(null);
    }
  };

  const preCfg = config?.preSeasonPredictions || {};
  const poCfg = config?.playoffPredictions || {};
  const AFC = getTeamsByConference('AFC');
  const NFC = getTeamsByConference('NFC');
  const busy = laeuft !== null;

  if (!config) {
    return <div className="bonus-auswertung__leer">Für diese Saison sind keine Bonusfragen konfiguriert.</div>;
  }

  return (
    <div className="bonus-auswertung">
      <div className="bonus-auswertung__schritte">
        <button type="button" className="ba-button" onClick={handleVorschlag} disabled={busy}>
          {laeuft === 'vorschlag' ? '⏳ Lade…' : '🔮 Vorschlag aus Spieldaten laden'}
        </button>
        <span className="ba-hinweis">
          Füllt alles, was aus Tabelle und Playoff-Spielen folgt. MVP bleibt manuell.
          {gespeicherteLoesung?.gespeichertAm && ` Letzte gespeicherte Lösung: ${new Date(gespeicherteLoesung.gespeichertAm).toLocaleString('de-DE')}.`}
        </span>
      </div>

      {(hinweise.length > 0 || quelle) && (
        <div className="ba-box">
          {quelle && <div className="ba-quelle">Quelle: {quelle === 'espn-tabelle' ? 'ESPN-Tabelle (Seeds) + Spiel-Docs' : 'nur Spiel-Docs (Bilanzen geschätzt)'}</div>}
          <ul>{hinweise.map((h, i) => <li key={i}>{h}</li>)}</ul>
        </div>
      )}

      {/* ---------- Pre-Season ---------- */}
      <h3 className="ba-titel">📅 Pre-Season – richtige Antworten</h3>

      {preCfg.divisionWinners?.aktiv && (
        <div className="ba-frage">
          <h4>🏆 Division Winners</h4>
          <div className="ba-grid ba-grid--4">
            {Object.entries(DIVISIONS).map(([name, teams]) => (
              <TeamAuswahl key={name} label={name} value={pre[divKey(name)]} teams={teams} onChange={v => setPre(divKey(name), v)} />
            ))}
          </div>
        </div>
      )}

      {preCfg.conferenceSeed1?.aktiv && (
        <div className="ba-frage">
          <h4>1️⃣ #1 Seeds</h4>
          <div className="ba-grid ba-grid--2">
            <TeamAuswahl label="AFC #1" value={pre.afcSeed1} teams={AFC} onChange={v => setPre('afcSeed1', v)} />
            <TeamAuswahl label="NFC #1" value={pre.nfcSeed1} teams={NFC} onChange={v => setPre('nfcSeed1', v)} />
          </div>
        </div>
      )}

      {preCfg.wildCardTeams?.aktiv && (
        <div className="ba-frage">
          <h4>🃏 Wild Cards</h4>
          <div className="ba-grid ba-grid--3">
            {[1, 2, 3].map(n => (
              <TeamAuswahl key={`a${n}`} label={`AFC WC ${n}`} value={pre[`afcWildCard${n}`]} teams={AFC}
                ausschliessen={[1, 2, 3].filter(m => m !== n).map(m => pre[`afcWildCard${m}`]).filter(Boolean)}
                onChange={v => setPre(`afcWildCard${n}`, v)} />
            ))}
            {[1, 2, 3].map(n => (
              <TeamAuswahl key={`n${n}`} label={`NFC WC ${n}`} value={pre[`nfcWildCard${n}`]} teams={NFC}
                ausschliessen={[1, 2, 3].filter(m => m !== n).map(m => pre[`nfcWildCard${m}`]).filter(Boolean)}
                onChange={v => setPre(`nfcWildCard${n}`, v)} />
            ))}
          </div>
        </div>
      )}

      {preCfg.mvp?.aktiv && (
        <div className="ba-frage">
          <h4>⭐ MVP (manuell)</h4>
          <div className="ba-grid ba-grid--2">
            <TeamAuswahl label="Team" value={pre.mvpTeam} teams={Object.keys(NFL_TEAMS)}
              onChange={v => { setProbe(null); setAntworten(a => ({ ...a, preSeasonPredictions: { ...a.preSeasonPredictions, mvpTeam: v, mvpPlayer: '', mvpPlayerName: '' } })); }} />
            <div>
              <span className="team-auswahl__label">Spieler</span>
              {!pre.mvpTeam ? <div className="ba-hinweis">Erst Team wählen.</div>
                : rosterLaedt ? <div className="ba-hinweis">⏳ Lade Kader…</div>
                : roster.length > 0 ? (
                  <select className="spieler-select" value={pre.mvpPlayer || ''} onChange={e => {
                    const p = roster.find(r => r.id === e.target.value);
                    setProbe(null);
                    setAntworten(a => ({ ...a, preSeasonPredictions: { ...a.preSeasonPredictions, mvpPlayer: e.target.value, mvpPlayerName: p?.name || '' } }));
                  }}>
                    <option value="">-- Spieler wählen --</option>
                    {roster.map(p => <option key={p.id} value={p.id}>#{p.number} {p.name} ({p.position})</option>)}
                  </select>
                ) : <div className="ba-hinweis">Kader nicht abrufbar{pre.mvpPlayerName ? ` – gespeichert: ${pre.mvpPlayerName}` : ''}.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ---------- Playoffs ---------- */}
      <h3 className="ba-titel">🏆 Playoffs – richtige Antworten</h3>

      {poCfg.conferenceChampionshipTeams?.aktiv && (
        <div className="ba-frage">
          <h4>🏈 Conference Championship Teams</h4>
          <div className="ba-grid ba-grid--4">
            <TeamAuswahl label="AFC 1" value={po.afcChampionship1} teams={AFC} ausschliessen={[po.afcChampionship2].filter(Boolean)} onChange={v => setPo('afcChampionship1', v)} />
            <TeamAuswahl label="AFC 2" value={po.afcChampionship2} teams={AFC} ausschliessen={[po.afcChampionship1].filter(Boolean)} onChange={v => setPo('afcChampionship2', v)} />
            <TeamAuswahl label="NFC 1" value={po.nfcChampionship1} teams={NFC} ausschliessen={[po.nfcChampionship2].filter(Boolean)} onChange={v => setPo('nfcChampionship1', v)} />
            <TeamAuswahl label="NFC 2" value={po.nfcChampionship2} teams={NFC} ausschliessen={[po.nfcChampionship1].filter(Boolean)} onChange={v => setPo('nfcChampionship2', v)} />
          </div>
        </div>
      )}

      {poCfg.superBowlTeams?.aktiv && (
        <div className="ba-frage">
          <h4>🏆 Super Bowl Teams</h4>
          <div className="ba-grid ba-grid--2">
            <TeamAuswahl label="AFC Champion" value={po.superBowlAFC} teams={AFC} onChange={v => setPo('superBowlAFC', v)} />
            <TeamAuswahl label="NFC Champion" value={po.superBowlNFC} teams={NFC} onChange={v => setPo('superBowlNFC', v)} />
          </div>
        </div>
      )}

      {(poCfg.superBowlWinner?.aktiv || preCfg.superBowlWinner?.aktiv) && (
        <div className="ba-frage">
          <h4>👑 Super Bowl Champion (gilt für Pre-Season und Playoffs)</h4>
          <div className="ba-grid ba-grid--2">
            <TeamAuswahl label="Champion" value={po.superBowlWinner} teams={Object.keys(NFL_TEAMS)}
              onChange={v => { setProbe(null); setAntworten(a => ({
                preSeasonPredictions: { ...a.preSeasonPredictions, superBowlWinner: v },
                playoffPredictions: { ...a.playoffPredictions, superBowlWinner: v }
              })); }} />
          </div>
        </div>
      )}

      {/* ---------- Probelauf & Auswertung ---------- */}
      <div className="bonus-auswertung__aktionen">
        <button type="button" className="ba-button" onClick={handleProbe} disabled={busy}>
          {laeuft === 'probe' ? '⏳ Rechne…' : '🧪 Probelauf (nichts schreiben)'}
        </button>
        {!bestaetigen ? (
          <button type="button" className="ba-button ba-button--rot" onClick={() => setBestaetigen(true)} disabled={busy || !probe}>
            🧮 Auswerten und Punkte schreiben
          </button>
        ) : (
          <span className="ba-bestaetigen">
            Überschreibt die Bonusfragen-Punkte aller Spieler dieser Saison.
            <button type="button" className="ba-button ba-button--rot" onClick={handleAuswerten} disabled={busy}>
              {laeuft === 'auswerten' ? '⏳ Schreibe…' : 'Ja, auswerten'}
            </button>
            <button type="button" className="ba-button" onClick={() => setBestaetigen(false)} disabled={busy}>Abbrechen</button>
          </span>
        )}
        {!probe && <span className="ba-hinweis">Erst Probelauf, dann Auswerten.</span>}
      </div>

      {status && <div className="ba-status">{status}</div>}

      {probe && (
        <table className="ba-tabelle">
          <thead>
            <tr><th>Spieler</th><th>Pre-Season</th><th>Playoffs</th><th>Gesamt</th></tr>
          </thead>
          <tbody>
            {[...probe].sort((a, b) => b.gesamt - a.gesamt).map(e => (
              <tr key={e.userId}>
                <td>{e.name || e.userId}</td>
                <td>{e.preSeasonPunkte}</td>
                <td>{e.playoffPunkte}</td>
                <td><strong>{e.gesamt}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default BonusAuswertung;
